<template>
  <div class="card border-0 shadow-none rounded-3 mb-4 settings-card">
    <div class="card-body p-4 p-xl-40">
      <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <span class="settings-chip">{{ $t("system_settings_page.general_setup") }}</span>
          <h4 class="mb-2 text-title fw-semibold mt-3">
            {{ $t("system_settings_page.system_settings_title") }}
          </h4>
          <p class="text-optional mb-0 settings-subtitle">
            {{ $t("system_settings_page.system_settings_description") }}
          </p>
        </div>
        <button type="button" class="btn style-five px-4" :disabled="isSaving" @click="$emit('submit')">
          <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
          {{ $t("system_settings_page.save_settings") }}
        </button>
      </div>

      <div class="row g-4">
        <div class="col-lg-12">
          <div class="row g-4">
            <div class="col-md-6">
              <label class="settings-label">{{ $t("system_settings_page.company_name") }}</label>
              <input :value="settings.site_name" @input="patchSettings({ site_name: (($event.target as HTMLInputElement).value) })" type="text" class="form-control settings-input" :placeholder="$t('system_settings_page.company_name_placeholder')" />
            </div>
            <div class="col-md-6">
              <label class="settings-label">{{ $t("system_settings_page.company_phone") }}</label>
              <input :value="settings.company_phone" @input="patchSettings({ company_phone: (($event.target as HTMLInputElement).value) })" type="text" class="form-control settings-input" :placeholder="$t('system_settings_page.company_phone_placeholder')" />
            </div>
            <div class="col-md-6">
              <label class="settings-label">{{ $t("system_settings_page.company_email") }}</label>
              <input :value="settings.company_email" @input="patchSettings({ company_email: (($event.target as HTMLInputElement).value) })" type="email" class="form-control settings-input" :placeholder="$t('system_settings_page.company_email_placeholder')" />
            </div>
            <div class="col-md-6">
              <label class="settings-label">{{ $t("system_settings_page.timezone") }}</label>
              <input :value="settings.timezone" @input="patchSettings({ timezone: (($event.target as HTMLInputElement).value) })" type="text" class="form-control settings-input" :placeholder="$t('system_settings_page.timezone_placeholder')" />
            </div>
            <div class="col-12">
              <label class="settings-label">{{ $t("system_settings_page.company_address") }}</label>
              <textarea :value="settings.company_address" @input="patchSettings({ company_address: (($event.target as HTMLTextAreaElement).value) })" rows="3" class="form-control settings-input settings-textarea" :placeholder="$t('system_settings_page.company_address_placeholder')"></textarea>
            </div>
            <div class="col-md-4">
              <label class="settings-label">{{ $t("system_settings_page.default_currency") }}</label>
              <select :value="settings.default_currency" @change="patchSettings({ default_currency: (($event.target as HTMLSelectElement).value) })" class="form-select settings-input">
                <option value="USD">{{ $t("system_settings_page.currency_usd") }}</option>
                <option value="KHR">{{ $t("system_settings_page.currency_khr") }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="settings-label">{{ $t("system_settings_page.default_language") }}</label>
              <select :value="settings.language" @change="patchSettings({ language: (($event.target as HTMLSelectElement).value) })" class="form-select settings-input">
                <option value="en">{{ $t("system_settings_page.language_en") }}</option>
                <option value="km">{{ $t("system_settings_page.language_km") }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="settings-label">{{ $t("system_settings_page.date_format") }}</label>
              <input :value="settings.dateformat" @input="patchSettings({ dateformat: (($event.target as HTMLInputElement).value) })" type="text" class="form-control settings-input" :placeholder="$t('system_settings_page.date_format_placeholder')" />
            </div>
            <div class="col-md-6">
              <label class="settings-label">{{ $t("system_settings_page.default_customer") }}</label>
              <select :value="settings.default_customer ?? ''" @change="patchSettings({ default_customer: parseNullableNumber(($event.target as HTMLSelectElement).value) })" class="form-select settings-input">
                <option :value="null">{{ $t("system_settings_page.walk_in_customer") }}</option>
                <option v-for="customer in customers" :key="customer.id" :value="customer.id">
                  {{ customer.name }}
                </option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="settings-label">{{ $t("system_settings_page.default_warehouse") }}</label>
              <select :value="settings.default_warehouse ?? ''" @change="patchSettings({ default_warehouse: parseNullableNumber(($event.target as HTMLSelectElement).value) })" class="form-select settings-input">
                <option :value="null">{{ $t("system_settings_page.no_default_warehouse") }}</option>
                <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
                  {{ warehouse.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="col-lg-12">
          <div class="settings-panel h-100">
            <div class="logo-upload-box row">
              <div class="logo-preview-area col-md-5">
                <img
                  v-if="logoUrl"
                  :src="logoUrl"
                  :alt="$t('system_settings_page.company_logo')"
                  class="logo-preview-image"
                />
                <div v-else class="logo-preview-empty">
                  <i class="ri-image-line"></i>
                  <span>{{ $t("system_settings_page.no_logo") }}</span>
                </div>
              </div>
              <div class="col-md-7">
                <label class="settings-label">{{ $t("system_settings_page.company_logo") }}</label>
                <p class="text-optional small mb-3">
                  {{ $t("system_settings_page.company_logo_description") }}
                </p>
                <input type="file" class="form-control settings-input" accept="image/*" @change="onLogoChange" />
              </div>
            </div>

            <div class="settings-divider"></div>

            <h6 class="text-title fw-semibold mb-3">
              {{ $t("system_settings_page.reference_prefixes") }}
            </h6>
            <div class="row g-3">
              <div v-for="prefixField in prefixFields" :key="prefixField.key" class="col-sm-4 col-md-3">
                <label class="settings-label">{{ prefixField.label }}</label>
                <input
                  :value="settings[prefixField.key]"
                  @input="patchSettings({ [prefixField.key]: (($event.target as HTMLInputElement).value) } as Partial<Setting>)"
                  type="text"
                  class="form-control settings-input"
                  :placeholder="prefixField.placeholder"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";

import type { Customer } from "@/services/customer";
import type { Setting } from "@/services/setting";
import type { Warehouse } from "@/services/warehouse";

type PrefixFieldKey =
  | "sales_prefix"
  | "purchase_prefix"
  | "estimation_prefix"
  | "expense_prefix"
  | "income_prefix"
  | "stockAdjustment_prefix"
  | "stockTransfer_prefix"
  | "posinvoice_prefix"
  | "customer_prefix"
  | "supplier_prefix"
  | "products_prefix"
  | "transaction_prefix"
  | "employee_prefix";

export default defineComponent({
  name: "SystemSettings",
  emits: ["logo-change", "submit", "update:settings"],
  props: {
    settings: {
      type: Object as PropType<Setting>,
      required: true,
    },
    customers: {
      type: Array as PropType<Customer[]>,
      required: true,
    },
    warehouses: {
      type: Array as PropType<Warehouse[]>,
      required: true,
    },
    logoUrl: {
      type: String,
      default: "",
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();

    const prefixFields = computed<
      Array<{
        key: PrefixFieldKey;
        label: string;
        placeholder: string;
      }>
    >(() => [
      { key: "sales_prefix", label: t("system_settings_page.sales_prefix"), placeholder: "SALE-" },
      { key: "purchase_prefix", label: t("system_settings_page.purchase_prefix"), placeholder: "PUR-" },
      { key: "estimation_prefix", label: t("system_settings_page.quotation_prefix"), placeholder: "EST-" },
      { key: "expense_prefix", label: t("system_settings_page.expense_prefix"), placeholder: "EX-" },
      { key: "income_prefix", label: t("system_settings_page.income_prefix"), placeholder: "IN-" },
      { key: "stockAdjustment_prefix", label: t("system_settings_page.adjustment_prefix"), placeholder: "SA-" },
      { key: "stockTransfer_prefix", label: t("system_settings_page.transfer_prefix"), placeholder: "ST-" },
      { key: "posinvoice_prefix", label: t("system_settings_page.pos_prefix"), placeholder: "PINV" },
      { key: "customer_prefix", label: t("system_settings_page.customer_prefix"), placeholder: "CT-" },
      { key: "supplier_prefix", label: t("system_settings_page.supplier_prefix"), placeholder: "SUP-" },
      { key: "products_prefix", label: t("system_settings_page.product_prefix"), placeholder: "SKU-" },
      { key: "transaction_prefix", label: t("system_settings_page.transaction_prefix"), placeholder: "TRN-" },
      { key: "employee_prefix", label: t("system_settings_page.employee_prefix"), placeholder: "EMP-" },
    ]);

    const parseNullableNumber = (value: string) => {
      if (!value) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const patchSettings = (partial: Partial<Setting>) => {
      emit("update:settings", {
        ...props.settings,
        ...partial,
      });
    };

    const onLogoChange = (event: Event) => {
      const file = (event.target as HTMLInputElement)?.files?.[0] || null;
      emit("logo-change", file);
    };

    return {
      prefixFields,
      parseNullableNumber,
      patchSettings,
      onLogoChange,
    };
  },
});
</script>

<style scoped>
.settings-card {
  background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
}

.settings-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: rgba(96, 93, 255, 0.12);
  color: #605dff;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 14px;
}

.settings-subtitle {
  max-width: 700px;
}

.settings-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.settings-input {
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #ffffff;
  color: #0f172a;
  box-shadow: none;
  padding: 14px 18px;
}

.settings-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.settings-textarea {
  min-height: 110px;
  padding-top: 16px;
}

.settings-panel {
  border: 1px solid #e6ebff;
  border-radius: 24px;
  background: #f9fbff;
  padding: 24px;
}

.logo-upload-box {
  display: flex;
  /* gap: 20px; */
}

.logo-preview-area {
  height: 160px;
  border-radius: 20px;
  border: 1px dashed #cbd5f5;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.logo-preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.logo-preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #94a3b8;
}

.logo-preview-empty i {
  font-size: 40px;
}

.settings-divider {
  height: 1px;
  background: #dde4fb;
  margin: 24px 0;
}
</style>
